import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import flow from 'lodash/flow';
import { bindActionCreators } from 'redux';
import { logClientError, logClientInfo } from 'src/common/actions/logging';
import { providers } from 'src/common/utils/subscription/getSubscriptionProvider';
import SubscriptionServiceUnavailable from 'src/common/components/subscription/form/SubscriptionServiceUnavailable';
import { hasPairAuthFlowParam } from 'src/common/utils/auth/pairAuthFlowHelpers';
import { BaseLoader } from '../../components/Loader';
import trackingLog from '../../constants/payment/tracking';
import connectWithExperiments from '../connectWithExperiments';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import { WEB_RECURLY_LIBRARY_TIMEOUT } from '../../constants/experiments/config';
import ensureRecurlyJs from './ensureRecurlyJs';

const DEFAULT_ONREADY_TIMEOUT = 10000;

export default function withRecurlyTracking(RecurlyFormComponent) {
  class RecurlyTracking extends Component {
    static propTypes = {
      recurly: PropTypes.object.isRequired,
      recurlyLogging: PropTypes.object.isRequired,
      [WEB_RECURLY_LIBRARY_TIMEOUT]: PropTypes.number,
      setRecurlyErrorState: PropTypes.func.isRequired,
      setRecurlyLoadingState: PropTypes.func,
    };

    static contextType = LocationAndLocalizationContext;

    constructor(props) {
      super(props);
      this.state = {
        isReady: false,
        isReadyTimedOut: false,
      };
      this.configuredTimeout =
        props[WEB_RECURLY_LIBRARY_TIMEOUT] || DEFAULT_ONREADY_TIMEOUT;

      this.onReady = this.onReady.bind(this);
      this.onReadyTimeout = this.onReadyTimeout.bind(this);
    }

    componentDidMount() {
      this.monitorRecurlyOnReady();
    }

    componentWillUnmount() {
      // clear the timeout that is tracking the recurly.js 'ready' event.
      clearTimeout(this.readyTimeout);
    }

    onReady() {
      const { setRecurlyLoadingState } = this.props;

      // clear the timeout that is tracking the recurly.js 'ready' event.
      clearTimeout(this.readyTimeout);

      this.setState({ isReady: true });
      if (setRecurlyLoadingState) {
        setRecurlyLoadingState(false);
      }
      this.logOnReadyTiming();
    }

    onReadyTimeout() {
      const { setRecurlyErrorState } = this.props;
      // remove the on ready event handler
      this.props.recurly.off('ready', this.onReady);

      this.setState({ isReadyTimedOut: true });
      setRecurlyErrorState(true);
      this.logOnReadyTimeout('onReadyTimeout');
    }

    monitorRecurlyOnReady() {
      this.start = Date.now();
      this.readyTimeout = setTimeout(
        this.onReadyTimeout,
        this.configuredTimeout,
      );
      this.props.recurly.once('ready', this.onReady);
    }

    logOnReadyTiming() {
      const time = Date.now() - this.start;
      this.props.recurlyLogging.logClientInfo({
        message: `${trackingLog.labels.loadTime}.${providers.recurly}js::${time}ms`,
        context: {
          label: trackingLog.labels.loadTime,
          time,
        },
      });
    }

    logOnReadyTimeout(source) {
      this.props.recurlyLogging.logClientError({
        message: `${trackingLog.labels.paymentFormError}.${providers.recurly}js::onready-timeout`,
        context: {
          label: trackingLog.labels.paymentFormError,
          timeout: this.configuredTimeout,
          source,
        },
      });
    }

    render() {
      const { isReady, isReadyTimedOut } = this.state;
      const { location } = this.context;
      const isPairingFlow = hasPairAuthFlowParam(location?.query);

      return (
        <div>
          {
            // If recurly.js has not emitted a 'ready' event and we are still within the timeout
            // threshold, then assume that it is still setting up and show the loading state.
            !isReady && !isReadyTimedOut && <BaseLoader />
          }

          {
            // If we timed out waiting for the 'ready' event to be emitted then show the
            // 'Subscription Unavailable' messaging.
            !isReady && isReadyTimedOut && (
              <SubscriptionServiceUnavailable isPairingFlow={isPairingFlow} />
            )
          }

          <div
            data-testid="recurlyFormWrapper"
            style={{
              // If recurly.js isReady then show the RecurlyForm, otherwise keep it hidden until
              // we receive a 'ready' event.
              display: isReady && !isReadyTimedOut ? 'inherit' : 'none',
            }}
          >
            <RecurlyFormComponent {...this.props} />
          </div>
        </div>
      );
    }
  }

  function mapDispatchToProps(dispatch) {
    return {
      recurlyLogging: bindActionCreators(
        {
          logClientError,
          logClientInfo,
        },
        dispatch,
      ),
    };
  }

  return flow(
    connect(null, mapDispatchToProps),
    connectWithExperiments([WEB_RECURLY_LIBRARY_TIMEOUT]),
    ensureRecurlyJs,
  )(RecurlyTracking);
}
